<template>
  <div class="reporting">
    <div class="reporting-top-bar">
      <div class="reporting-date-range">
        <span class="material-icons">calendar_today</span>
        <span>{{ $t(`reporting.calendar.dateRange`) }}:</span>
        <DateRangePicker
          v-model="dateRange"
          :maxDate="new Date()"
          @update="loadData"
          opens="right"
          :localeData="localeData"
          :ranges="ranges"
        />
      </div>

      <div v-if="stats.order_count > 0" class="reporting-order-count">
        <button
          v-if="companyUser.company.settings.enable_gathering_order_check_action"
          @click="createIndustrySummaryExport"
          class="button button-green button-small button-round"
        >
          {{ $t('reporting.actions.createSummaryExport') }}
        </button>
        <span class="material-icons">format_list_bulleted</span>
        {{
          $t('reporting.actions.orderCount', {
            count: stats.order_count,
          })
        }}
      </div>
    </div>
    <LoadingSpinner v-if="loading" />
    <div v-else class="reporting-body">
      <div class="content-wrap">
        <header class="content-header cf">
          <h3>
            <span>{{ filters.site.name }}</span>
            <BreadCrumbSeparator />
            <span>{{ $t('reporting.actions.siteOverview') }}</span>
          </h3>
        </header>
        <div class="content-body">
          <div class="actions-data-wrapper">
            <div class="data-container">
              <h4 class="data-title">{{ $t('reporting.actions.containerStatsTitle') }}</h4>

              <div class="form-wrap" v-if="filters.cost_centers.length > 0" style="margin-bottom: 1rem">
                <v-select
                  class="form-select__select"
                  v-model="selectedCostCenterUuid"
                  :options="costCenterOptions"
                  :placeholder="$t('selects.costCenterSelectPlaceholder')"
                  :reduce="costCenter => costCenter.uuid"
                  :searchable="true"
                  :filterable="true"
                >
                </v-select>
              </div>

              <AreaSelect :areas="filters.areas" v-model="selectedAreaUuid" :disabled="disabled" />
              <div class="info-box">
                <span class="material-icons">info</span>
                <span>{{ $t('reporting.actions.areaSelectInfoText') }}</span>
              </div>
              <ZoneSelect
                v-if="selectedAreaUuid != null"
                :zones="this.filters.zones"
                v-model="selectedZoneUuid"
                :disabled="disabled"
                style="margin-bottom: 20px"
              />
              <ActionsDataBarChart
                :entries="stats.grouped_emptying_action_counts"
                :totalCount="stats.emptying_action_count"
                :title="selectedArea && selectedArea.name"
              />
            </div>
            <div class="data-chart">
              <h4 class="data-title">{{ $t('meta.fractions') }}</h4>
              <FractionsPieChart
                :entries="stats.fractions"
                :showLabelOnChart="this.companyForScreenshots.includes(this.companyUser.company_uuid)"
              />
            </div>
            <div class="site-plan">
              <h4 class="data-title">{{ $t('meta.sitePlan') }}</h4>
              <img v-if="sitePlan.src" :src="sitePlan.src" :alt="sitePlan.alt" @error="setAltImage" />
              <div v-else class="alert alert-info">
                <p>{{ $t('reporting.actions.sitePlanMissing') }}</p>
                <Link :to="sitePlan.link">{{ $t('reporting.actions.linkToUploadMissingSitePlan') }}</Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="bottom-bars-and-pies">
        <div
          v-if="companyUser.company.settings.enable_fraction_disposal_selection"
          class="content-wrap"
          style="flex-grow: 1"
        >
          <header class="content-header cf">
            <h3>
              <span>{{ $t('reporting.industry.fractionDisposals.title') }}</span>
            </h3>
          </header>

          <div class="content-body">
            <div style="display: flex; flex-direction: row; flex-wrap: wrap">
              <div style="flex-grow: 2; min-width: 320px">
                <FractionDisposalBarChart :fractionDisposals="stats.fraction_disposals" />
              </div>

              <div style="flex-grow: 1; min-width: 130px">
                <FractionDisposalPieChart
                  :fractionDisposals="stats.fraction_disposals"
                  :fractionDetails="stats.fraction_details"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="companyUser.company.settings.enable_gathering_order_check_action"
          class="content-wrap"
          style="flex-grow: 1"
        >
          <header class="content-header cf">
            <h3>
              <span>{{ $t('reporting.industry.checks.title') }}</span>
            </h3>
          </header>

          <div class="content-body">
            <ChecksBarChart :checkActionCount="stats.check_action_count" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import DateRangePicker from 'vue2-daterange-picker'

import { DATEPICKER_LOCALE_DATA, DATEPICKER_RANGES } from '@/views/config'

import BreadCrumbSeparator from '@/components/BreadCrumbs/BreadCrumbSeparator'
import LoadingSpinner from '@/components/LoadingSpinner/index.vue'
import ActionsDataBarChart from '@/components/Reporting/Industry/ActionsDataBarChart'
import AreaSelect from '@/components/Reporting/Industry/AreaSelect'
import ChecksBarChart from '@/components/Reporting/Industry/ChecksBarChart'
import FractionDisposalBarChart from '@/components/Reporting/Industry/FractionDisposalBarChart'
import FractionDisposalPieChart from '@/components/Reporting/Industry/FractionDisposalPieChart'
import FractionsPieChart from '@/components/Reporting/Industry/FractionsPieChart'
import ZoneSelect from '@/components/Reporting/Industry/ZoneSelect'

import exportsApi from '@/api/exports'
import reportingApi from '@/api/reporting'
import altImage from '@/assets/binity.svg'

export default {
  name: 'ReportingActions',
  inject: ['companyUser'],
  components: {
    ActionsDataBarChart,
    AreaSelect,
    ZoneSelect,
    BreadCrumbSeparator,
    ChecksBarChart,
    DateRangePicker,
    FractionsPieChart,
    FractionDisposalBarChart,
    FractionDisposalPieChart,
    LoadingSpinner,
  },
  data: () => ({
    companyForScreenshots: [
      'a7f3a51b-8b85-4d1a-a0ea-fb10c4408253', // For MAN CompanyUUID on Production
      '6d9edca7-e917-4d08-bc73-1d4c37a9420c', // For Testla CompanyUUID on Staging
      'a7f3a51b-8b85-4d1a-a0ea-fb10c4408253', // For LocalTesting # Replace: With any local companyUUID
    ],
    dateRange: { startDate: moment().subtract(1, 'months').toDate(), endDate: new Date() },
    selectedCostCenterUuid: null,
    selectedAreaUuid: null,
    selectedZoneUuid: null,
    filters: {
      order_count: 0,
      cost_centers: [],
    },
    stats: {},
    loading: true,
  }),
  computed: {
    localeData() {
      return DATEPICKER_LOCALE_DATA[this.companyUser.user.language]
    },
    ranges() {
      return DATEPICKER_RANGES[this.companyUser.user.language]
    },
    disabled() {
      return this.stats.emptying_action_count === 0
    },
    sitePlan() {
      if (this.selectedArea) {
        return {
          src: this.selectedArea.image?.url,
          alt: this.selectedArea.name,
          link: `/company/sites/${this.filters.site.uuid}/areas/${this.selectedArea.uuid}`,
        }
      } else {
        return {
          src: this.filters.site.image?.url,
          alt: this.filters.site.name,
          link: `/company/sites/${this.filters.site.uuid}`,
        }
      }
    },
    costCenterOptions() {
      return this.filters.cost_centers.map(function (costCenter) {
        return { label: costCenter.name, uuid: costCenter.uuid }
      })
    },
    selectedArea() {
      if (this.selectedAreaUuid) {
        return this.filters.areas.find(area => area.uuid === this.selectedAreaUuid)
      } else {
        return null
      }
    },
  },
  watch: {
    async selectedCostCenterUuid() {
      this.loadData()
      this.selectedAreaUuid = null
      this.selectedZoneUuid = null
    },
    async selectedAreaUuid() {
      this.loadData()
      this.selectedZoneUuid = null
    },
    async selectedZoneUuid() {
      // It should be fine to only load Stats and not Filters here
      await this.getStats()
    },
  },
  methods: {
    async loadData() {
      await this.getFilters()
      await this.getStats()
    },
    setAltImage(event) {
      event.target.src = altImage
    },
    async createIndustrySummaryExport() {
      await exportsApi.create({
        type: 'industry_summary_xlsx',
        company_uuid: this.companyUser.company_uuid,
        project_uuids: this.projectUuids,
        from: this.dateRange.startDate,
        to: this.dateRange.endDate,
      })
    },
    async getFilters() {
      this.filters = await reportingApi.getActionFilters({
        company_uuid: this.companyUser.company_uuid,
        from: this.dateRange.startDate,
        to: this.dateRange.endDate,
        cost_center_uuid: this.selectedCostCenterUuid,
        area_uuid: this.selectedAreaUuid,
      })
    },
    async getStats() {
      this.stats = await reportingApi.getActionStats({
        company_uuid: this.companyUser.company_uuid,
        from: this.dateRange.startDate,
        to: this.dateRange.endDate,
        cost_center_uuid: this.selectedCostCenterUuid,
        area_uuid: this.selectedAreaUuid,
        zone_uuid: this.selectedZoneUuid,
      })
    },
  },
  async mounted() {
    await this.loadData()
    this.loading = false
  },
}
</script>

<style lang="scss" scoped>
@import 'reporting.scss';

.reporting {
  .reporting-order-count {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
  .content-header {
    h3 {
      display: flex;
      align-items: center;
    }
  }
  .data-container,
  .site-plan,
  .data-chart {
    padding-bottom: 2rem;
  }
  .data-container {
    grid-area: data;
    min-height: 250px;
    .info-box {
      display: flex;
      align-items: flex-start;
      gap: 0.375rem;
      margin: 0.75rem 0 1.5rem 0;
      color: var(--button-disabled-font-color);
      font-size: 0.825rem;
      .material-icons {
        font-size: 1.125rem;
      }
    }
  }
  .site-plan {
    grid-area: image;
    img {
      display: block;
      max-width: 100%;
      height: auto;
      margin: 0 auto;
    }
  }
  .data-chart {
    grid-area: chart;
    min-height: 500px;
    max-height: 900px;
  }

  #bottom-bars-and-pies {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 20px;
  }

  h4.data-title {
    margin-top: 0;
    margin-bottom: 1.5rem;
    font-size: 1.25rem;
  }

  .actions-data-wrapper {
    display: grid;
    grid-gap: 1rem;
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'data'
      'chart'
      'image';
  }

  @media (min-width: 600px) {
    .actions-data-wrapper {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-template-areas:
        'data chart'
        'image image';
    }
  }

  @media (min-width: 992px) {
    .actions-data-wrapper {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      grid-template-areas: 'data image chart';
    }
  }
}
</style>
